const BASE_DEFAULT_ACTIVITY_TYPE_CONFIG = Object.freeze({
  visibilityMode: 'base',
  responsibleQuestionMode: 'uebungsleiter',
  responsibleQuestionText: '',
  eventContextMode: 'veranstaltung',
  timeQuestionText: '',
  timeEntryMode: 'preset-or-custom',
  autoResponsibleMode: 'none',
  pauseMode: 'hidden-no-deduction',
  pdfProtocolMode: 'hidden',
  serviceDurationLabel: 'Dienstzeit (abzgl. Pause)',
  protocolCode: '',
  scaleWorkHours: false,
  keywordMode: 'optional',
  hoursQuestionMode: 'required',
});

const ENTRY_TYPE_DEFAULT_OVERRIDES = Object.freeze({
  'Übung im 24h-Dienst': {
    eventContextMode: 'uebung',
  },
  'Einsatz im 24h-Dienst': {
    responsibleQuestionMode: 'einheitsfuehrer',
    eventContextMode: 'einsatz',
    timeEntryMode: 'custom-only',
  },
  'Geräteprüfung im 24h-Dienst': {
    responsibleQuestionMode: 'einheitsfuehrer',
    eventContextMode: 'geraetepruefung',
    timeEntryMode: 'custom-only',
    autoResponsibleMode: 'current-user',
    serviceDurationLabel: 'Dienstzeit',
    hoursQuestionMode: 'hidden',
  },
  'Tagesfortbildung': {
    visibilityMode: 'instructor',
    timeEntryMode: 'a3-za3',
    pauseMode: 'question',
    pdfProtocolMode: 'ask',
    scaleWorkHours: true,
  },
  'Tag einer Wochenfortbildung': {
    visibilityMode: 'instructor',
    timeEntryMode: 'a3-za3',
    pauseMode: 'question',
    pdfProtocolMode: 'ask',
    scaleWorkHours: true,
  },
  'Pflichtfortbildung': {
    visibilityMode: 'instructor',
    timeEntryMode: 'a3-za3',
    pauseMode: 'question',
    pdfProtocolMode: 'ask',
    scaleWorkHours: true,
  },
  'Tag eines Wettkampfes': {
    visibilityMode: 'instructor',
    responsibleQuestionMode: 'einheitsfuehrer',
    timeEntryMode: 'a3-za3',
    pauseMode: 'question',
    pdfProtocolMode: 'ask',
    scaleWorkHours: true,
  },
  'Trainingstag für einen Wettkampf': {
    visibilityMode: 'instructor',
    timeEntryMode: 'a3-za3',
    pauseMode: 'question',
    pdfProtocolMode: 'ask',
    scaleWorkHours: true,
  },
  'Dozententätigkeit (Tagesdienst)': {
    visibilityMode: 'instructor',
    timeEntryMode: 'te1-flex-honorar',
    pauseMode: 'question',
    pdfProtocolMode: 'ask',
    scaleWorkHours: true,
  },
  'Geräteprüfung (Tagesdienst)': {
    visibilityMode: 'equipment-day',
    responsibleQuestionMode: 'geraetepruefer',
    timeEntryMode: 'te1-flex-honorar',
    pauseMode: 'question',
    pdfProtocolMode: 'ask',
    scaleWorkHours: true,
    hoursQuestionMode: 'hidden',
  },
  'Honorartätigkeit': {
    visibilityMode: 'instructor',
    timeEntryMode: 'te1-flex-honorar',
    pauseMode: 'hidden-no-deduction',
    pdfProtocolMode: 'hidden',
    serviceDurationLabel: 'Dienstzeit',
    scaleWorkHours: true,
  },
  'Dozententätigkeit im 24h-Dienst': {
    visibilityMode: 'instructor',
    timeEntryMode: 'custom-only',
  },
  'Ausbilderfortbildung (Intern)': {
    visibilityMode: 'instructor',
    timeEntryMode: 'a3-za3',
    pauseMode: 'question',
    pdfProtocolMode: 'ask',
    scaleWorkHours: true,
  },
  'Schulung durch externen Ausbilder': {
    visibilityMode: 'instructor',
    timeEntryMode: 'a3-za3',
    pauseMode: 'question',
    pdfProtocolMode: 'ask',
    scaleWorkHours: true,
  },
  'Externe Fortbildung': {
    visibilityMode: 'base',
    responsibleQuestionText: '3. Wer hat die Fortbildung durchgeführt?',
    timeEntryMode: 'a3-za3',
    pauseMode: 'question',
    pdfProtocolMode: 'ask',
    scaleWorkHours: true,
  },
  'Einsatz nach Alarm a.d. Freizeit': {
    responsibleQuestionMode: 'einheitsfuehrer',
    eventContextMode: 'einsatz',
    timeEntryMode: 'custom-only',
    protocolCode: 'Alarm a.d. Freizeit',
    serviceDurationLabel: 'Dienstzeit',
    pdfProtocolMode: 'ask',
  },
  'Fortbildung Geräteprüfer': {
    visibilityMode: 'base',
    timeEntryMode: 'a3-za3',
    pauseMode: 'question',
    pdfProtocolMode: 'ask',
    scaleWorkHours: true,
  },
  'Grundausbildungslehrgang SRHT': {
    visibilityMode: 'srht-training',
    responsibleQuestionMode: 'lehrgangsleiter',
    timeEntryMode: 'srht-basic-course',
    pauseMode: 'hidden-no-deduction',
    pdfProtocolMode: 'hidden',
    serviceDurationLabel: 'Dienstzeit',
    hoursQuestionMode: 'hidden',
  },
});

function normalizeText(value, fallback = '') {
  const normalized = String(value || '').trim();
  return normalized || fallback;
}

function normalizeBoolean(value, fallback = false) {
  if (typeof value === 'boolean') {
    return value;
  }

  const normalized = String(value || '').trim().toLowerCase();
  if (['1', 'true', 'yes', 'ja'].includes(normalized)) {
    return true;
  }
  if (['0', 'false', 'no', 'nein'].includes(normalized)) {
    return false;
  }

  return fallback;
}

function deriveSupportFlags(timeEntryMode) {
  if (timeEntryMode === 'a3-za3') {
    return {
      isDayService: true,
      supportsA3Za3: true,
      supportsTGroup: false,
      supportsHonorar: false,
    };
  }

  if (timeEntryMode === 'te1-flex-honorar') {
    return {
      isDayService: true,
      supportsA3Za3: false,
      supportsTGroup: true,
      supportsHonorar: true,
    };
  }

  return {
    isDayService: false,
    supportsA3Za3: false,
    supportsTGroup: false,
    supportsHonorar: false,
  };
}

export function getDefaultActivityTypeConfig(entryType = '') {
  const normalizedEntryType = String(entryType || '').trim();
  return {
    ...BASE_DEFAULT_ACTIVITY_TYPE_CONFIG,
    ...(ENTRY_TYPE_DEFAULT_OVERRIDES[normalizedEntryType] || {}),
  };
}

export function resolveActivityTypeConfig(rawConfig = {}, entryType = '') {
  const fallback = getDefaultActivityTypeConfig(entryType || rawConfig?.name || rawConfig?.value || '');
  const timeEntryMode = normalizeText(rawConfig.timeEntryMode, fallback.timeEntryMode);

  return {
    visibilityMode: normalizeText(rawConfig.visibilityMode, fallback.visibilityMode),
    responsibleQuestionMode: normalizeText(rawConfig.responsibleQuestionMode, fallback.responsibleQuestionMode),
    responsibleQuestionText: normalizeText(rawConfig.responsibleQuestionText, fallback.responsibleQuestionText),
    eventContextMode: normalizeText(rawConfig.eventContextMode, fallback.eventContextMode),
    timeQuestionText: normalizeText(rawConfig.timeQuestionText, fallback.timeQuestionText),
    timeEntryMode,
    autoResponsibleMode: normalizeText(rawConfig.autoResponsibleMode, fallback.autoResponsibleMode),
    pauseMode: normalizeText(rawConfig.pauseMode, fallback.pauseMode),
    pdfProtocolMode: normalizeText(rawConfig.pdfProtocolMode, fallback.pdfProtocolMode),
    serviceDurationLabel: normalizeText(rawConfig.serviceDurationLabel, fallback.serviceDurationLabel),
    protocolCode: normalizeText(rawConfig.protocolCode, fallback.protocolCode),
    scaleWorkHours: normalizeBoolean(rawConfig.scaleWorkHours, fallback.scaleWorkHours),
    keywordMode: normalizeText(rawConfig.keywordMode, fallback.keywordMode),
    hoursQuestionMode: normalizeText(rawConfig.hoursQuestionMode, fallback.hoursQuestionMode),
    ...deriveSupportFlags(timeEntryMode),
  };
}

export function buildFallbackActivityTypeOption(entryType) {
  const config = resolveActivityTypeConfig({}, entryType);
  return {
    value: entryType,
    label: entryType,
    isDayService: config.isDayService,
    supportsA3Za3: config.supportsA3Za3,
    supportsTGroup: config.supportsTGroup,
    supportsHonorar: config.supportsHonorar,
    keywordMode: config.keywordMode,
    hoursQuestionMode: config.hoursQuestionMode,
    visibilityMode: config.visibilityMode,
    responsibleQuestionMode: config.responsibleQuestionMode,
    responsibleQuestionText: config.responsibleQuestionText,
    eventContextMode: config.eventContextMode,
    timeQuestionText: config.timeQuestionText,
    timeEntryMode: config.timeEntryMode,
    autoResponsibleMode: config.autoResponsibleMode,
    pauseMode: config.pauseMode,
    pdfProtocolMode: config.pdfProtocolMode,
    serviceDurationLabel: config.serviceDurationLabel,
    protocolCode: config.protocolCode,
    scaleWorkHours: config.scaleWorkHours,
  };
}